// src/scenes/invoice-mobilescan/index.jsx
import React, { useState, useEffect, useRef } from "react";
import {
    AppBar,
    Toolbar,
    Typography,
    Box,
    Button,
    Fab,
    Card,
    CardMedia,
    CardContent,
    CardActions,
    IconButton,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    LinearProgress,
    Snackbar,
    useTheme,
    useMediaQuery,
} from "@mui/material";
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    CloudUpload as UploadIcon,
    Refresh as RetryIcon,
} from "@mui/icons-material";
import axios from "axios";
import { v4 as uuidv4 } from "uuid";
import Cropper from "react-easy-crop";

// Endpoints
const API_SCHOOL_LIST = "https://figazuresql.swancreate.com/api/swa/SchoolMst";
const API_UPLOAD_INVOICES = "https://invoice.swancreate.com/api/upload/invoices";
const API_SCAN_INVOICE = "https://invoice.swancreate.com/scan_invoice/";
const API_AZ_INVOICE = "https://figazuresql.swancreate.com/api/swa/AZ_Invoice";
const API_AZ_INVOICE_ITEMS = "https://figazuresql.swancreate.com/api/swa/AZ_InvoiceItems";

// Cropping helpers
async function createImage(url) {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = "anonymous";
        img.src = url;
        img.onload = () => resolve(img);
        img.onerror = e => reject(e);
    });
}
async function getCroppedBlob(imageSrc, crop) {
    const image = await createImage(imageSrc);
    const canvas = document.createElement("canvas");
    canvas.width = crop.width;
    canvas.height = crop.height;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(
        image,
        crop.x, crop.y, crop.width, crop.height,
        0, 0, canvas.width, canvas.height
    );
    return new Promise(resolve => canvas.toBlob(resolve, "image/jpeg"));
}

export default function MobileInvoiceScannerPage() {
    const theme = useTheme();
    const isSmUp = useMediaQuery(theme.breakpoints.up("sm"));

    // Site selector
    const [schools, setSchools] = useState([]);
    const [selectedSchoolId, setSelectedSchoolId] = useState("");
    const [selectedSchoolName, setSelectedSchoolName] = useState("");
    const [selectedDistrictId, setSelectedDistrictId] = useState("");

    // Cropping
    const [filesToCrop, setFilesToCrop] = useState([]);
    const [cropIndex, setCropIndex] = useState(0);
    const [crop, setCrop] = useState({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(1);
    const [croppedArea, setCroppedArea] = useState(null);

    // Scanned items & upload
    const [items, setItems] = useState([]);
    const [editItem, setEditItem] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);

    // Snackbar
    const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

    // Hidden file input ref
    const fileInputRef = useRef(null);

    // Load schools
    useEffect(() => {
        axios.get(API_SCHOOL_LIST)
            .then(res => setSchools(res.data))
            .catch(console.error);
    }, []);

    // Site selection
    const handleSelectSchool = e => {
        const id = e.target.value;
        setSelectedSchoolId(id);
        const s = schools.find(x => String(x.SchoolId) === id);
        if (s) {
            setSelectedSchoolName(s.SchoolName);
            setSelectedDistrictId(s.SchoolDistrictId);
        } else {
            setSelectedSchoolName("");
            setSelectedDistrictId("");
        }
    };

    // File input
    const onFilesSelected = e => {
        const selected = Array.from(e.target.files || []);
        if (selected.length) {
            setFilesToCrop(selected);
            setCropIndex(0);
        }
        // clear so same file can be reselected
        e.target.value = null;
    };

    // Cropping callbacks
    const onCropComplete = (_, pixels) => setCroppedArea(pixels);
    const cancelCrop = () => { setFilesToCrop([]); setCropIndex(0); };
    const finishCrop = async () => {
        const raw = filesToCrop[cropIndex];
        const url = URL.createObjectURL(raw);
        const blob = await getCroppedBlob(url, croppedArea);
        const file = new File([blob], raw.name, { type: "image/jpeg" });
        const thumb = URL.createObjectURL(file);

        setItems(prev => [
            ...prev,
            {
                id: uuidv4(),
                file,
                url: thumb,
                metadata: { InvoiceNumber: "", InvoiceDate: "", VendorName: "" },
                status: "pending",
                error: null,
                scanData: null
            }
        ]);

        if (cropIndex + 1 < filesToCrop.length) {
            setCropIndex(i => i + 1);
        } else {
            cancelCrop();
        }
    };

    // Item actions
    const removeItem = id => setItems(p => p.filter(x => x.id !== id));
    const openEdit = it => setEditItem({ ...it });
    const saveEdit = () => {
        setItems(p => p.map(x => x.id === editItem.id ? editItem : x));
        setEditItem(null);
    };
    const retryItem = idx => {
        setItems(p => {
            const c = [...p];
            c[idx].status = "pending";
            c[idx].error = null;
            return c;
        });
        processSingle(idx);
    };

    // Upload & scan a single page: FIXED
    const processSingle = async idx => {
        let it = { ...items[idx], status: "uploading", error: null };
        setItems(p => p.map((x, i) => i === idx ? it : x));

        // 1) upload to blob storage
        const uploadFD = new FormData();
        uploadFD.append("file", it.file);
        uploadFD.append("SchoolId", selectedSchoolId);
        uploadFD.append("SchoolName", selectedSchoolName);
        await axios.post(API_UPLOAD_INVOICES, uploadFD, {
            headers: { Accept: "application/json" }
        });

        // 2) scan → OCR
        const scanFD = new FormData();
        // <------ KEY CHANGE HERE: use "document"
        scanFD.append("document", it.file);
        scanFD.append("SchoolId", selectedSchoolId);
        try {
            const res = await axios.post(API_SCAN_INVOICE, scanFD);
            if (res.data.success && Array.isArray(res.data.invoice_data)) {
                const d = res.data.invoice_data[0] || {};
                it = {
                    ...it,
                    status: "scanned",
                    scanData: res.data.invoice_data,
                    metadata: {
                        InvoiceNumber: d.InvoiceNumber || "",
                        VendorName: d.VendorName || "",
                        InvoiceDate: d.InvoiceDate || ""
                    }
                };
            } else {
                throw new Error(res.data.message || "No invoice data returned");
            }
        } catch (e) {
            it = { ...it, status: "error", error: e.message };
        }
        setItems(p => p.map((x, i) => i === idx ? it : x));
    };

    // Export to AZ_Invoice & Items
    const exportToDB = async allData => {
        for (const inv of allData) {
            const hdr = {
                apiid: inv.id,
                InvoiceNumber: inv.metadata.InvoiceNumber,
                VendorName: inv.metadata.VendorName,
                InvoiceDate: inv.metadata.InvoiceDate,
                SiteID: selectedSchoolId,
                SiteName: selectedSchoolName,
                SchoolDistrictID: selectedDistrictId,
                CreatedOn: new Date().toISOString(),
                isReviewed: false
            };
            await axios.post(API_AZ_INVOICE, hdr);
            if (Array.isArray(inv.Items)) {
                for (const item of inv.Items) {
                    await axios.post(API_AZ_INVOICE_ITEMS, {
                        apiid: uuidv4(),
                        InvoiceApiId: inv.id,
                        InvoiceNumber: inv.metadata.InvoiceNumber,
                        Description: item.Description || "",
                        Quantity: item.Quantity || 0,
                        Unit: item.Unit || "",
                        UnitPrice: item.UnitPrice || 0,
                        ItemDate: item.ItemDate || null,
                        Tax: item.Tax || "",
                        Amount: item.Amount || 0,
                        CreatedOn: new Date().toISOString(),
                        isReviewed: false
                    });
                }
            }
        }
    };

    // Upload & Process All
    const handleUploadAll = async () => {
        if (!selectedSchoolId) {
            return setSnackbar({ open: true, message: "Select a site first", severity: "error" });
        }
        if (!items.length) {
            return setSnackbar({ open: true, message: "No pages to upload", severity: "warning" });
        }

        setUploading(true);
        let collected = [];
        for (let i = 0; i < items.length; i++) {
            await processSingle(i);
            if (items[i].scanData) collected.push(...items[i].scanData);
            setUploadProgress(i + 1);
        }
        await exportToDB(collected);

        setUploading(false);
        setItems([]);
        setTimeout(() => setUploadProgress(0), 300);
        setSnackbar({ open: true, message: "All invoices processed!", severity: "success" });
    };

    const fileToCrop = filesToCrop[cropIndex];

    return (
        <Box sx={{ pb: 8 }}>
            <AppBar position="sticky">
                <Toolbar>
                    <Typography variant="h6">Invoice Scanner</Typography>
                </Toolbar>
            </AppBar>

            <Box p={2}>
                {/* SITE SELECTOR */}
                <FormControl fullWidth margin="normal">
                    <InputLabel id="school-label">Select Site</InputLabel>
                    <Select
                        labelId="school-label"
                        value={selectedSchoolId}
                        onChange={handleSelectSchool}
                    >
                        {schools.map(s => (
                            <MenuItem key={s.SchoolId} value={String(s.SchoolId)}>
                                {s.SchoolName}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                {/* SCANNED CARDS */}
                {items.map((it, idx) => (
                    <Card key={it.id} sx={{ mb: 1 }}>
                        <CardMedia component="img" height="140" image={it.url} />
                        <CardContent>
                            <Typography variant="subtitle2" noWrap>
                                {it.metadata.InvoiceNumber || "<pending>"} /
                                {it.metadata.VendorName || "—"}
                            </Typography>
                            {it.status === "error" && (
                                <Typography color="error" variant="caption">{it.error}</Typography>
                            )}
                        </CardContent>
                        <CardActions>
                            <IconButton onClick={() => openEdit(it)}><EditIcon /></IconButton>
                            {it.status === "error" && (
                                <IconButton onClick={() => retryItem(idx)}><RetryIcon /></IconButton>
                            )}
                            <IconButton onClick={() => removeItem(it.id)}><DeleteIcon /></IconButton>
                        </CardActions>
                    </Card>
                ))}

                {/* UPLOAD & PROGRESS */}
                <Box mt={2} textAlign="center">
                    <Button
                        variant="contained"
                        color="success"
                        startIcon={<UploadIcon />}
                        onClick={handleUploadAll}
                        disabled={uploading || !items.length}
                    >
                        {uploading
                            ? `Processing ${uploadProgress}/${items.length}`
                            : "Upload & Process"}
                    </Button>
                    {uploading && (
                        <LinearProgress
                            variant="determinate"
                            value={(uploadProgress / items.length) * 100}
                            sx={{ mt: 1 }}
                        />
                    )}
                </Box>

                {/* SOLID GREEN CAMERA BUTTON */}
                <Box mt={2} textAlign="center">
                    <Button
                        variant="contained"
                        color="success"
                        sx={{ px: 4 }}
                        onClick={() => fileInputRef.current.click()}
                    >
                        Select or Capture Invoices
                    </Button>
                </Box>
            </Box>

            {/* HIDDEN FILE INPUT */}
            <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                multiple
                style={{ display: "none" }}
                onChange={onFilesSelected}
            />

            {/* CROP DIALOG */}
            <Dialog open={!!fileToCrop} onClose={cancelCrop} fullWidth maxWidth="sm">
                <DialogTitle>Crop Invoice Page</DialogTitle>
                <DialogContent sx={{ position: "relative", height: isSmUp ? 400 : 300 }}>
                    {fileToCrop && (
                        <Cropper
                            image={URL.createObjectURL(fileToCrop)}
                            crop={crop}
                            zoom={zoom}
                            aspect={8.5 / 11}
                            onCropChange={setCrop}
                            onZoomChange={setZoom}
                            onCropComplete={onCropComplete}
                        />
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={cancelCrop}>Cancel</Button>
                    <Button onClick={finishCrop} variant="contained">Next</Button>
                </DialogActions>
            </Dialog>

            {/* METADATA EDITOR */}
            {editItem && (
                <Dialog open fullWidth maxWidth="xs" onClose={() => setEditItem(null)}>
                    <DialogTitle>Edit Invoice Metadata</DialogTitle>
                    <DialogContent>
                        <TextField
                            fullWidth margin="dense" label="Invoice Number"
                            value={editItem.metadata.InvoiceNumber}
                            onChange={e => setEditItem({
                                ...editItem,
                                metadata: { ...editItem.metadata, InvoiceNumber: e.target.value }
                            })}
                        />
                        <TextField
                            fullWidth margin="dense" label="Invoice Date" type="date"
                            InputLabelProps={{ shrink: true }}
                            value={editItem.metadata.InvoiceDate}
                            onChange={e => setEditItem({
                                ...editItem,
                                metadata: { ...editItem.metadata, InvoiceDate: e.target.value }
                            })}
                        />
                        <TextField
                            fullWidth margin="dense" label="Vendor"
                            value={editItem.metadata.VendorName}
                            onChange={e => setEditItem({
                                ...editItem,
                                metadata: { ...editItem.metadata, VendorName: e.target.value }
                            })}
                        />
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setEditItem(null)}>Cancel</Button>
                        <Button onClick={saveEdit} variant="contained">Save</Button>
                    </DialogActions>
                </Dialog>
            )}

            {/* FAB */}
            <Fab
                color="success"
                aria-label="scan"
                onClick={() => fileInputRef.current.click()}
                sx={{ position: "fixed", bottom: theme.spacing(2), right: theme.spacing(2) }}
            >
                <AddIcon />
            </Fab>

            {/* SNACKBAR */}
            <Snackbar
                open={snackbar.open}
                message={snackbar.message}
                autoHideDuration={3000}
                onClose={() => setSnackbar(s => ({ ...s, open: false }))}
            />
        </Box>
    );
}
